// Build timestamp: 2025-12-22-21-05-DEBUG-VERSION
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
} from "react-router-dom";
import "./styles.css";

import ChatBoard from "./components/ChatBoard";
import { Admin } from "./components/Admin";
import { Broadcast } from "./components/Broadcast";
import { ICPTesting } from "./components/ICPTesting";
import { ICPTestingAudit } from "./components/ICPTestingAudit";
import ResponseTimeDashboard from "./components/ResponseTimeDashboard";
import SalesReviewDashboard from "./components/SalesReviewDashboard";
import { Chat, WebSocketEvent } from "./types/chat";
import { orchestratorApi, setAccessTokenFn } from "./api/orchestrator";
import { Auth0Provider } from "./auth/Auth0Provider";
import { AuthGuard } from "./auth/AuthGuard";
import { UserProfile } from "./components/UserProfile";
import { useUserRoles } from "./auth/useUserRoles";
import { useAuth0 } from "@auth0/auth0-react";

// Wrapper component to apply conditional className based on route
const MainContentWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isFullWidth = location.pathname === '/' ||
    location.pathname === '/admin' ||
    location.pathname.startsWith('/icp-testing') ||
    location.pathname.startsWith('/analytics');

  return (
    <main className={`main-content${isFullWidth ? ' full-width' : ''}`}>
      {children}
    </main>
  );
};

const App: React.FC = () => {
  const [wsStatus, setWsStatus] = useState<
    "connecting" | "connected" | "disconnected"
  >("connecting");
  const [chats, setChats] = useState<Chat[]>([]);
  const [loading, setLoading] = useState(true);
  const wsRef = useRef<WebSocket | null>(null);
  const scrollPositionRef = useRef<number>(0);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState<string>(() => {
    // Initialize from localStorage to maintain entity selection across page reloads
    const stored = localStorage.getItem('selectedEntityId');
    return stored || '';
  });

  // Debug initial App state
  console.log('[App] App component mounting - Initial state:', {
    wsStatus,
    loading,
    selectedEntityId,
    chatsLength: chats.length
  });

  // Handle entity changes from ChatBoard user selection
  const handleEntityChange = useCallback((entityId: string) => {
    console.log('[App] handleEntityChange called with:', { entityId });

    // Only update if entityId is valid (not empty)
    if (entityId) {
      console.log('[App] Setting selectedEntityId to:', entityId);
      setSelectedEntityId(entityId);
      // Save to localStorage for persistence across page reloads
      localStorage.setItem('selectedEntityId', entityId);
    } else {
      console.log('[App] Ignoring empty entity ID');
    }
  }, []);

  // Load chats when entity is selected
  useEffect(() => {
    console.log('[App] Entity useEffect triggered:', {
      selectedEntityId,
      hasEntity: !!selectedEntityId,
      willLoadChats: !!selectedEntityId
    });

    // Only load chats when we have a selected entity
    if (selectedEntityId) {
      console.log('[App] Calling loadChats for entity:', selectedEntityId);
      loadChats();
    } else {
      console.log('[App] Skipping loadChats - no entity selected');
    }
  }, [selectedEntityId]); // Reload chats when entity changes

  // Mobile height fix - force chat container heights on mobile devices
  useEffect(() => {
    const fixMobileHeights = () => {
      if (window.innerWidth <= 768) {
        // Fix parent containers first
        const mainContent = document.querySelector('.main-content') as HTMLElement;
        if (mainContent) {
          mainContent.style.height = '100vh';
          mainContent.style.minHeight = '100vh';
          mainContent.style.flex = '1';
        }

        const boardColumns = document.querySelector('.board-columns') as HTMLElement;
        if (boardColumns) {
          boardColumns.style.height = '90vh';
          boardColumns.style.minHeight = '90vh';
          boardColumns.style.flex = '1';
        }

        const boardColumnElements = document.querySelectorAll('.board-column');
        boardColumnElements.forEach((column) => {
          const columnElement = column as HTMLElement;
          columnElement.style.height = '88vh';
          columnElement.style.minHeight = '88vh';
          columnElement.style.maxHeight = 'none';
          columnElement.style.display = 'flex';
          columnElement.style.flexDirection = 'column';
        });

        // Now fix chat lists
        const chatLists = document.querySelectorAll('.chat-list');
        chatLists.forEach((element) => {
          const htmlElement = element as HTMLElement;
          htmlElement.style.height = '85vh';
          htmlElement.style.minHeight = '85vh';
          htmlElement.style.maxHeight = 'none';
          htmlElement.style.overflowY = 'auto';
          htmlElement.style.display = 'flex';
          htmlElement.style.flexDirection = 'column';
          htmlElement.style.flex = '1';
        });
      }
    };

    // Fix heights on load and resize
    fixMobileHeights();
    window.addEventListener('resize', fixMobileHeights);

    // Also fix heights after a delay to catch dynamically loaded content
    const timeouts = [500, 1000, 2000].map(delay =>
      setTimeout(fixMobileHeights, delay)
    );

    return () => {
      window.removeEventListener('resize', fixMobileHeights);
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  // Setup WebSocket connection
  useEffect(() => {
    const connectWebSocket = () => {
      const ws = orchestratorApi.createWebSocket();
      wsRef.current = ws;
      // Make WebSocket accessible globally for ChatDetail component
      (window as any).wsConnection = ws;

      ws.onopen = () => {
        console.log("WebSocket connected");
        setWsStatus("connected");
      };

      ws.onmessage = (event) => {
        try {
          const wsEvent: WebSocketEvent = JSON.parse(event.data);
          handleWebSocketEvent(wsEvent);
        } catch (error) {
          console.error("Failed to parse WebSocket message:", error);
        }
      };

      ws.onclose = () => {
        console.log("WebSocket disconnected");
        setWsStatus("disconnected");
        // Clear global reference
        (window as any).wsConnection = null;

        // Attempt to reconnect after 3 seconds
        setTimeout(() => {
          setWsStatus("connecting");
          connectWebSocket();
        }, 3000);
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        setWsStatus("disconnected");
      };
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      // Clear global reference
      (window as any).wsConnection = null;
    };
  }, []);

  // Helper function to sort chats by most recent activity
  const sortChatsByRecentActivity = (chats: Chat[]): Chat[] => {
    return [...chats].sort((a, b) => {
      // Use last_message_at if available, otherwise fall back to updated_at, then created_at
      const timeA = a.last_message_at || a.updated_at || a.created_at;
      const timeB = b.last_message_at || b.updated_at || b.created_at;
      return new Date(timeB).getTime() - new Date(timeA).getTime();
    });
  };

  // Enhanced scroll position preservation using useCallback
  const loadChats = useCallback(
    async (preserveScrollPosition = false, loadMore = false, entityId?: string) => {
      // Use provided entityId or fall back to current state
      const targetEntityId = entityId || selectedEntityId;

      console.log(`[App] loadChats called:`, {
        preserveScrollPosition,
        loadMore,
        entityId,
        targetEntityId,
        selectedEntityId,
        currentChatsCount: chats.length
      });

      // If entityId is provided (e.g., from sync_complete event), update selectedEntityId
      if (entityId && entityId !== selectedEntityId) {
        console.log(`[App] Updating selectedEntityId from ${selectedEntityId} to ${entityId}`);
        setSelectedEntityId(entityId);
      }

      // Save current scroll position if requested
      if (preserveScrollPosition) {
        scrollPositionRef.current = window.scrollY;
      }

      if (loadMore) {
        setLoadingMore(true);
      }

      try {
        const limit = loadMore ? 50 : 100; // Load 100 initially, 50 more when loading more
        const offset = loadMore ? chats.length : 0; // Start from current chat count when loading more

        console.log(`[App] Fetching chats with:`, { limit, offset, targetEntityId });

        const chatsData = await orchestratorApi.getChats(
          undefined,
          limit,
          offset,
          targetEntityId,
        );

        console.log(`[App] Received ${chatsData.length} chats from API`, { chatsData });

        if (loadMore) {
          // When loading more, append to existing chats (avoid duplicates)
          setChats((prevChats) => {
            const existingIds = new Set(prevChats.map((chat) => chat.chat_id));
            const newChats = chatsData.filter(
              (chat) => !existingIds.has(chat.chat_id),
            );
            return [...prevChats, ...newChats];
          });
          // Check if we got fewer chats than requested (means no more available)
          setHasMoreChats(chatsData.length === limit);
        } else {
          // Initial load or refresh - replace all chats
          setChats(chatsData);
          // If we got fewer than 100 chats, there are no more
          setHasMoreChats(chatsData.length === 100);
        }

        // Restore scroll position after data is loaded and DOM is updated
        if (preserveScrollPosition && scrollPositionRef.current > 0) {
          // Use requestAnimationFrame for better timing with DOM updates
          requestAnimationFrame(() => {
            setTimeout(() => {
              window.scrollTo({
                top: scrollPositionRef.current,
                behavior: "instant", // Instant scroll to avoid visual jumps
              });
            }, 50); // Slightly longer delay to ensure all DOM updates are complete
          });
        }
      } catch (error) {
        console.error("Failed to load chats:", error);
      } finally {
        setLoading(false);
        if (loadMore) {
          setLoadingMore(false);
        }
      }
    },
    [selectedEntityId],
  );

  const loadMoreChats = useCallback(async () => {
    if (hasMoreChats && !loadingMore) {
      await loadChats(false, true);
    }
  }, [hasMoreChats, loadingMore, loadChats]);

  const loadAllChats = useCallback(async () => {
    if (!loadingMore) {
      setLoadingMore(true);
      try {
        // Load all chats with no limit for the selected entity
        // Fixed: parameters are (status, limit, offset, entityId), not (entityId, limit, offset)
        const chatsData = await orchestratorApi.getChats(
          undefined,  // status
          10000,      // limit
          0,          // offset
          selectedEntityId  // entityId
        );
        setChats(chatsData);
        setHasMoreChats(false); // No more chats to load after getting all
      } catch (error) {
        console.error("Failed to load all chats:", error);
      } finally {
        setLoadingMore(false);
      }
    }
  }, [loadingMore, selectedEntityId]);

  const loadChatsWithLimit = useCallback(async (limit: number) => {
    if (!loadingMore) {
      setLoadingMore(true);
      try {
        const chatsData = await orchestratorApi.getChats(
          undefined,
          limit,
          0,
          selectedEntityId
        );
        setChats(chatsData);
        setHasMoreChats(chatsData.length === limit);
      } catch (error) {
        console.error("Failed to load chats with limit:", error);
      } finally {
        setLoadingMore(false);
      }
    }
  }, [loadingMore, selectedEntityId]);

  const handleWebSocketEvent = (wsEvent: WebSocketEvent) => {
    console.log("WebSocket event:", wsEvent);

    const payload: any = wsEvent?.data ?? wsEvent;

    switch (wsEvent.type) {
      case "message_received":
        // Update or add chat when new message is received
        if (payload) {
          handleMessageReceived(payload);
        }
        break;

      case "message_deleted":
        // Messages deleted from WhatsApp mobile - dispatch event for ChatMessages
        if (payload) {
          console.log('[App] Message deleted WebSocket event:', payload);
          window.dispatchEvent(new CustomEvent('message_deleted', {
            detail: { type: wsEvent.type, data: payload }
          }));
        }
        break;

      case "message_sent":
        // Update or add chat when new message is sent
        if (payload) {
          handleMessageReceived(payload);
        }
        break;

      case "chat_status_changed":
        // Update chat status
        if (payload) {
          handleChatStatusChanged(payload);
        }
        break;

      case "agent2_qualification":
      case "chat_handover":
        // Refresh chats when agent actions occur (preserve scroll position)
        loadChats(true);
        break;

      case "agent1_forwarded":
        // Agent 1 forwarded an event - refresh chats to show updates (preserve scroll position)
        loadChats(true);
        break;

      case "sync_progress":
      case "sync_error":
      case "tagging_complete":
      case "tagging_error":
        window.dispatchEvent(
          new CustomEvent("sync-job-update", { detail: wsEvent }),
        );
        break;

      case "sync_complete":
        // Sync completed - reload chats to show newly synced chats
        const syncEntityId = wsEvent.data?.entityId;
        console.log('[App] sync_complete WebSocket event:', {
          syncEntityId,
          currentSelectedEntityId: selectedEntityId,
          shouldReload: syncEntityId ? syncEntityId === selectedEntityId : 'global reload'
        });

        if (syncEntityId) {
          // Only reload chats if sync is for current entity
          if (syncEntityId === selectedEntityId) {
            console.log('[App] Sync for current entity, reloading chats');
            loadChats(true, false, syncEntityId);
          } else {
            console.log('[App] Sync for different entity, skipping reload');
          }
        } else {
          console.log('[App] Global sync completed, reloading all chats');
          loadChats(true);
        }
        // Dispatch event to ChatBoard for UI updates (isSyncing state)
        window.dispatchEvent(
          new CustomEvent("sync-job-update", { detail: wsEvent }),
        );
        break;

      case "chats_batch_synced":
      case "chats_tags_updated":
        // Progressive chat loading - refresh chats when batches are synced
        loadChats(true);
        break;

      case "chat_tags_updated":
        // Single chat tags updated - refresh chats to show new tags
        loadChats(true);
        break;
      case "broadcast_progress":
        // Dispatch broadcast progress events for the Broadcast component
        window.dispatchEvent(
          new CustomEvent("websocket-event", { detail: wsEvent }),
        );
        break;

      case "message.in":
        // New inbound message - handle similar to message_received
        if (payload) {
          handleMessageReceived(payload);
        }
        break;

      case "message.out":
        if (payload) {
          handleMessageReceived(payload);
        }
        break;

      default:
        // Log other events for debugging
        console.log("Unhandled WebSocket event:", wsEvent.type);
    }
  };

  const handleMessageReceived = (eventData: any) => {
    if (!eventData) {
      console.warn("[App] handleMessageReceived: No eventData provided");
      return;
    }

    const normalizedEntityId = eventData.entity_id ?? eventData.entityId;
    const normalizedChatId = eventData.chat_id ?? eventData.chatId;
    const normalizedTimestamp =
      eventData.ts ?? eventData.timestamp ?? eventData.created_at;
    const normalizedBody =
      eventData.message?.body ??
      eventData.body ??
      "";

    // IMPORTANT: Only process messages for the currently selected entity
    // This prevents cross-entity chat contamination in the UI
    const messageEntityId = normalizedEntityId;
    if (messageEntityId && messageEntityId !== selectedEntityId) {
      console.log("[App] Ignoring message for different entity:", {
        messageEntityId,
        selectedEntityId,
        chatId: normalizedChatId
      });
      return;
    }

    const chatId = normalizedChatId;
    console.log("[App] handleMessageReceived:", {
      chatId,
      eventType: eventData.type,
      hasMessage: !!eventData.message || !!eventData.body,
      messageBody: normalizedBody.substring(0, 50),
      ts: normalizedTimestamp,
      messageEntityId,
      selectedEntityId
    });

    // Dispatch custom event for ChatMessages component to listen to
    window.dispatchEvent(new CustomEvent('whatsappMessageReceived', {
      detail: { chatId, eventData }
    }));

    setChats((prevChats) => {
      const existingChatIndex = prevChats.findIndex(
        (c) => c.chat_id === chatId,
      );

      console.log("[App] Chat lookup:", {
        chatId,
        found: existingChatIndex >= 0,
        totalChats: prevChats.length,
        existingChatIndex,
      });

      if (existingChatIndex >= 0) {
        // Update existing chat and move to top
        const updatedChats = [...prevChats];
        const updatedChat = {
          ...updatedChats[existingChatIndex],
          last_message: normalizedBody || updatedChats[existingChatIndex].last_message,
          last_message_at: normalizedTimestamp || updatedChats[existingChatIndex].last_message_at,
          message_count: updatedChats[existingChatIndex].message_count + 1,
          updated_at: normalizedTimestamp || updatedChats[existingChatIndex].updated_at, // Update timestamp to ensure proper sorting
        };

        console.log("[App] Updating existing chat and moving to top:", {
          chatId,
          previousIndex: existingChatIndex,
          newLastMessage: updatedChat.last_message?.substring(0, 50),
        });

        // Remove chat from current position and add to top
        updatedChats.splice(existingChatIndex, 1);
        return [updatedChat, ...updatedChats];
      } else {
        // Add new chat at the top
        console.log("[App] Adding new chat to top:", {
          chatId,
          phoneNumber: eventData.phone_number,
        });

        const newChat: Chat = {
          chat_id: chatId,
          phone_number: eventData.phone_number,
          status: "unqualified",
          agent2_locked: false,
          owner: null,
          message_count: 1,
          last_message: normalizedBody,
          last_message_at: normalizedTimestamp,
          created_at: normalizedTimestamp,
          updated_at: normalizedTimestamp,
          control_status: "agent",
          qualifier_agent_enabled: false, // Default to false for new chats during development
        };
        return [newChat, ...prevChats];
      }
    });
  };

  const handleChatStatusChanged = (statusData: any) => {
    setChats((prevChats) => {
      const updatedChats = prevChats.map((chat) =>
        chat.chat_id === statusData.chat_id
          ? {
            ...chat,
            status: statusData.new_status,
            owner: statusData.owner,
            agent2_locked: statusData.agent2_locked,
            control_status:
              statusData.new_status === "handover"
                ? ("human" as const)
                : ("agent" as const),
            updated_at: statusData.updated_at,
          }
          : chat,
      );
      // Re-sort after status change to maintain order
      return sortChatsByRecentActivity(updatedChats);
    });
  };

  // Removed loading screen to allow ChatBoard to mount and initialize entity
  // ChatBoard will handle its own loading state

  return (
    <Auth0Provider>
      <AuthenticatedApp
        chats={chats}
        wsStatus={wsStatus}
        loadChats={loadChats}
        loadMoreChats={loadMoreChats}
        loadAllChats={loadAllChats}
        loadChatsWithLimit={loadChatsWithLimit}
        hasMoreChats={hasMoreChats}
        loadingMore={loadingMore}
        selectedEntityId={selectedEntityId}
        onSelectedEntityChange={handleEntityChange}
      />
    </Auth0Provider>
  );
};

// Navigation component - extracted for use in AuthenticatedApp
const Navigation: React.FC = () => {
  const location = useLocation();
  const { isAdmin } = useUserRoles();

  return (
    <nav className="main-nav">
      <div className="nav-brand">
        <h1>ForceMX WhatsApp</h1>
      </div>
      <div className="nav-links">
        <Link
          to="/"
          className={
            location.pathname === "/" ? "nav-link active" : "nav-link"
          }
        >
          📋 Chat Board
        </Link>
        <Link
          to="/analytics/response-times"
          className={
            location.pathname === "/analytics/response-times" ? "nav-link active" : "nav-link"
          }
        >
          📊 Response Times
        </Link>
        <Link
          to="/analytics/sales-review/prab?from=2026-06-01&to=2026-07-03"
          className={
            location.pathname.startsWith("/analytics/sales-review") || location.pathname === "/analytics/prab-sales-review"
              ? "nav-link active"
              : "nav-link"
          }
        >
          🧭 Sales Review
        </Link>
        {isAdmin && (
          <Link
            to="/broadcast"
            className={
              location.pathname === "/broadcast" ? "nav-link active" : "nav-link"
            }
          >
            📡 Broadcast
          </Link>
        )}
        {isAdmin && (
          <Link
            to="/admin"
            className={
              location.pathname === "/admin" ? "nav-link active" : "nav-link"
            }
          >
            ⚙️ Admin
          </Link>
        )}
      </div>
      <div className="nav-user">
        <UserProfile />
      </div>
    </nav>
  );
};

/**
 * 🏢 MULTI-TENANT SAAS: Inner component with Auth0 context
 *
 * This component has access to the Auth0 context and can:
 * 1. Set up the access token function for authenticated API calls
 * 2. Pass the token to all orchestratorApi calls for proper tenant_id extraction
 */
const AuthenticatedApp: React.FC<{
  chats: Chat[];
  wsStatus: "connecting" | "connected" | "disconnected";
  loadChats: (preserveScrollPosition?: boolean, loadMore?: boolean, entityId?: string) => Promise<void>;
  loadMoreChats: () => void;
  loadAllChats: () => void;
  loadChatsWithLimit: (limit: number) => void;
  hasMoreChats: boolean;
  loadingMore: boolean;
  selectedEntityId: string;
  onSelectedEntityChange: (entityId: string) => void;
}> = (props) => {
  const { getAccessTokenSilently } = useAuth0();

  // 🏢 MULTI-TENANT SAAS: Set up access token function for API calls
  // Called synchronously during render (not in useEffect) so the token function
  // is registered before any child component's useEffect fires. React runs child
  // effects before parent effects, so a useEffect here would be too late.
  setAccessTokenFn(getAccessTokenSilently);

  return (
    <AuthGuard
      requiredRoles={[
        // Canonical roles only (lowercase, no tenant names)
        // Tenant is provided separately via tenant_id claim in JWT
        "admin",
        "ops",
        "sales",
        "team"
      ]}
    >
      <Router>
        <div className="app">
          <Navigation />
          <MainContentWrapper>
            <Routes>
              <Route
                path="/"
                element={
                  <ChatBoard
                    chats={props.chats}
                    wsStatus={props.wsStatus}
                    onRefreshChats={() => props.loadChats()}
                    onRefreshChatsWithEntityId={(entityId) => props.loadChats(false, false, entityId)}
                    hasMoreChats={props.hasMoreChats}
                    loadingMore={props.loadingMore}
                    onLoadMoreChats={props.loadMoreChats}
                    onLoadAllChats={props.loadAllChats}
                    onLoadChatsWithLimit={props.loadChatsWithLimit}
                    selectedEntityId={props.selectedEntityId}
                    onSelectedEntityChange={props.onSelectedEntityChange}
                  />
                }
              />
              <Route
                path="/broadcast"
                element={
                  <AuthGuard requiredRoles={["admin"]}>
                    <Broadcast />
                  </AuthGuard>
                }
              />
              <Route
                path="/admin"
                element={
                  <AuthGuard requiredRoles={["admin"]}>
                    <Admin />
                  </AuthGuard>
                }
              />
              <Route
                path="/icp-testing"
                element={
                  <AuthGuard requiredRoles={["admin"]}>
                    <ICPTesting />
                  </AuthGuard>
                }
              />
              <Route
                path="/icp-testing/audit"
                element={
                  <AuthGuard requiredRoles={["admin"]}>
                    <ICPTestingAudit />
                  </AuthGuard>
                }
              />
              <Route
                path="/analytics/response-times"
                element={
                  <AuthGuard requiredRoles={["admin", "ops", "sales"]}>
                    <ResponseTimeDashboard />
                  </AuthGuard>
                }
              />
              <Route
                path="/analytics/prab-sales-review"
                element={
                  <AuthGuard requiredRoles={["admin", "ops", "sales"]}>
                    <Navigate
                      to="/analytics/sales-review/prab?from=2026-06-01&to=2026-07-03"
                      replace
                    />
                  </AuthGuard>
                }
              />
              <Route
                path="/analytics/sales-review/prab"
                element={
                  <AuthGuard requiredRoles={["admin", "ops", "sales"]}>
                    <SalesReviewDashboard />
                  </AuthGuard>
                }
              />
              <Route
                path="/analytics/sales-review/:rep"
                element={
                  <AuthGuard requiredRoles={["admin", "ops", "sales"]}>
                    <SalesReviewDashboard />
                  </AuthGuard>
                }
              />
            </Routes>
          </MainContentWrapper>
        </div>
      </Router>
    </AuthGuard>
  );
};

export default App;
